// Making things, on the Commons: the market's stalls on the plaza, and the
// wilds past it, an island of four places where materials are gathered (the
// woodlot, the quarry, the meadow and the scrapyard). commons.ts places them;
// this file builds them. World tiles, like the rest of the Commons.
import * as THREE from 'three';
import { mat, cyl, sphere, rbox, at } from './kit.js';
import { wbox } from './house.js';
import { surf } from './surfaces.js';
import * as Y from './yard.js';
import { ITEMS } from './catalog.js';
import { letters, type P } from './place.js';
import { rng, shade } from './util.js';

// ---------------------------------------------------------------------------
// where things are

// the wilds: their own island east of the Commons, over a plank bridge,
// four places round a crossing of paths
export const WILDS = { x0: 88, z0: 1, x1: 140, z1: 57 };
export const CROSSING: P = [115, 31.6];
export const PLACES: Record<string, { x0: number; z0: number; x1: number; z1: number; title: string; tag: string; color: string; work: P[]; masses: { x: number; y: number; w: number; d: number }[] }> = {
  woodlot: { x0: 90, z0: 3, x1: 113, z1: 29.5, title: 'THE WOODLOT', tag: 'timber · sticks', color: '#5d7a3f',
    work: [[101.5, 19.5], [104.5, 22], [98.5, 23], [107, 18.5], [95.5, 20], [102, 25.5]],
    masses: [{ x: 15.5, y: 20, w: 4, d: 2.4 }] },
  quarry: { x0: 117, z0: 3, x1: 138, z1: 29.5, title: 'THE QUARRY', tag: 'stone · clay · sand', color: '#8c8a83',
    work: [[123.5, 15.5], [128, 16], [132.5, 15.8], [124, 22.5], [134.5, 23.5], [129, 26]],
    masses: [{ x: 0, y: 1.4, w: 21, d: 7.8 }] },
  meadow: { x0: 90, z0: 34, x1: 113, z1: 55, title: 'THE MEADOW', tag: 'flax · wool · flowers', color: '#6f8f4a',
    work: [[106, 38.6], [109.5, 39.2], [104.8, 45.5], [108.5, 47.5], [105.5, 51.5], [110, 51]],
    masses: [{ x: 0.5, y: 0.5, w: 1, d: 1 }] },
  scrapyard: { x0: 117, z0: 34, x1: 138, z1: 55, title: 'THE SCRAPYARD', tag: 'iron · tin · copper', color: '#7a5a40',
    work: [[124, 41.5], [128.5, 39.5], [126, 46.5], [131.5, 45.5], [122.5, 50], [134, 50.5]],
    masses: [{ x: 13, y: 5, w: 6, d: 4 }] },
};
// the market: a row of stalls each side of the hall's carpet, facing the fountain
export const STALLS: P[] = [[31.2, 24.4], [34.0, 24.4], [36.8, 24.4], [43.2, 24.4], [46.0, 24.4], [48.8, 24.4]];

// ---------------------------------------------------------------------------
// materials, as they look in a crate or a heap

export const MATERIAL_COLOUR: Record<string, string> = {
  timber: '#8a5a3a', stick: '#7a5235', stone: '#a29f97', clay: '#c0643f', sand: '#e3c48f', flax: '#7f97d6', wool: '#f4f0e6',
  flowers: '#e86a92', iron: '#5d5f63', tin: '#b9bec4', copper: '#c27a4a', feathers: '#efe9dc', seeds: '#9a7a52', cutting: '#5f8f4a',
  sapling: '#5f8f4a', 'tulip-bulbs': '#c9a77a', compost: '#4a3a2a', planks: '#c9a06a', posts: '#b88a5a', cloth: '#d9c7a8', rope: '#c8a878',
  brick: '#a8503a', 'clay-tile': '#c46a44', ceramic: '#efe9df', glass: '#a9d6e0', dye: '#8a5aa8', paint: '#e2b04a', brass: '#d4a94a',
  enamel: '#e9eef2', wheels: '#6e4b33', bulbs: '#fff1b0',
};
const ROUND = new Set(['stone', 'clay', 'wool', 'flowers', 'seeds', 'tulip-bulbs', 'compost', 'sand', 'copper', 'bulbs', 'glass', 'dye', 'paint']);
const LONG = new Set(['timber', 'stick', 'posts', 'flax', 'cutting', 'sapling', 'feathers']);

// a little heap of a material: logs, stones, planks, puffs
export function heap(kind: string, n = 7, size = 0.12, seed = 1) {
  const g = new THREE.Group(), r = rng(seed + kind.length * 13);
  const c = MATERIAL_COLOUR[kind] || '#b8a68a';
  const m = kind === 'glass' ? mat(c, { rough: 0.1, opacity: 0.75 }) : kind === 'bulbs' ? mat(c, { emissive: '#ffd36b', emissiveIntensity: 1.2 }) : mat(c, { rough: kind === 'copper' || kind === 'brass' ? 0.35 : 0.85, metal: ['copper', 'brass', 'tin', 'iron'].includes(kind) ? 0.4 : 0 });
  for (let i = 0; i < n; i++) {
    const a = r() * Math.PI * 2, d = r() * size * 1.2, y = (i / n) * size * 1.4;
    let o: THREE.Object3D;
    if (kind === 'flowers') o = sphere(size * 0.45, mat(['#e86a92', '#f2c14e', '#ffffff', '#9b7fc9', '#e25d6a'][i % 5], { rough: 0.8 }), 8, 6);
    else if (ROUND.has(kind)) o = sphere(size * (0.45 + r() * 0.25), m, 7, 5);
    else if (LONG.has(kind)) { o = cyl(size * 0.22, size * 0.24, size * 2.6, m, 7); o.rotation.z = Math.PI / 2; o.rotation.y = r() * 0.6 - 0.3; }
    else if (kind === 'wheels') { o = new THREE.Mesh(new THREE.TorusGeometry(size * 0.6, size * 0.14, 6, 14), m); o.rotation.x = Math.PI / 2; }
    else if (kind === 'rope') { o = new THREE.Mesh(new THREE.TorusGeometry(size * 0.55, size * 0.16, 6, 14), m); o.rotation.x = Math.PI / 2; }
    else o = rbox(size * 1.9, size * 0.32, size * 0.8, size * 0.06, m);
    o.position.set(Math.cos(a) * d, size * 0.3 + y, Math.sin(a) * d);
    g.add(o);
  }
  return g;
}

function crate(kind: string, seed: number) {
  const g = new THREE.Group(), wood = mat('#b98a5a', { rough: 0.85 });
  g.add(at(rbox(0.46, 0.2, 0.34, 0.02, wood), 0, 0.1, 0));
  const fill = heap(kind, 6, 0.09, seed); fill.position.y = 0.12; g.add(fill);
  return g;
}

// ---------------------------------------------------------------------------
// a stall: a counter in the seller's colour, a striped awning, a name board,
// and what's for sale on top with a price tag each

export type StallItem = { n: number; price: number; words: string; material?: string; qty?: number; render?: string; maker?: string };
export type Stall = { seller: string; name: string; color?: string; items: StallItem[] };

const stripeTex = (a: string, b: string) => {
  const c = document.createElement('canvas'); c.width = 128; c.height = 8;
  const g = c.getContext('2d')!;
  for (let i = 0; i < 8; i++) { g.fillStyle = i % 2 ? b : a; g.fillRect(i * 16, 0, 16, 8); }
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; return t;
};

const fitted = (o: THREE.Object3D, max: number) => {
  const b = new THREE.Box3().setFromObject(o), s = new THREE.Vector3(); b.getSize(s);
  const k = max / Math.max(0.01, s.x, s.y, s.z);
  o.scale.multiplyScalar(k);
  return o;
};

// things that read well small on a counter; anything else (a sign waiting for its
// design, a tool) goes out as a parcel in brown paper
const SHOWN = new Set(['sofa', 'armchair', 'coffee-table', 'bookshelf', 'fireplace', 'floor-lamp', 'round-rug', 'fern', 'monstera', 'fiddle-leaf',
  'dining-set', 'sideboard', 'range', 'fridge', 'bed', 'nightstand', 'dresser', 'wardrobe', 'desk', 'claw-tub', 'pedestal-sink', 'hall-bench',
  'oak', 'cherry', 'apple', 'birch', 'pine', 'flowering-bush', 'flower-bed', 'veggie-box', 'lamp-post', 'birdbath', 'garden-bench', 'bins', 'mailbox',
  'lavender', 'rose-bush', 'sunflowers', 'hydrangea', 'heather']);

function parcel(seed: number) {
  const g = new THREE.Group(), r = rng(seed);
  const w = 0.34 + r() * 0.1, h = 0.22 + r() * 0.08, d = 0.26 + r() * 0.06;
  g.add(at(rbox(w, h, d, 0.02, mat('#c9a77a', { rough: 0.95 })), 0, h / 2, 0));
  const twine = mat('#8a5a3a', { rough: 0.8 });
  g.add(at(rbox(w + 0.01, 0.015, 0.03, 0.005, twine), 0, h + 0.002, 0), at(rbox(0.03, 0.015, d + 0.01, 0.005, twine), 0, h + 0.002, 0));
  for (const s of [-1, 1]) { const loop = new THREE.Mesh(new THREE.TorusGeometry(0.04, 0.012, 5, 10), twine); loop.position.set(s * 0.04, h + 0.03, 0); loop.rotation.y = s * 0.6; g.add(loop); }
  return g;
}

function priceTag(price: number) {
  const tag = new THREE.Mesh(new THREE.PlaneGeometry(0.3, 0.19),
    new THREE.MeshStandardMaterial({ map: letters([{ text: String(price), size: 70, color: '#3a2a20' }], 160, 100, '#fffaf0'), roughness: 0.9 }));
  tag.rotation.x = -0.35;
  return tag;
}

export function stallModel(s: Stall) {
  const g = new THREE.Group();
  const colour = s.color || '#e8a87c';
  const wood = mat('#8a6343', { rough: 0.8 }), top = surf({ kind: 'planks', color: '#c9a06a', board: 0.12 });
  const W = 2.3, D = 1.2;
  // the counter, its front painted in the seller's colour
  g.add(wbox(-W / 2, 0, -0.2, W / 2, 0.82, 0.32, mat(shade(colour, 0.25), { rough: 0.8 })));
  g.add(wbox(-W / 2 - 0.04, 0.82, -0.24, W / 2 + 0.04, 0.88, 0.38, top));
  g.add(wbox(-W / 2 + 0.06, 0.12, 0.321, W / 2 - 0.06, 0.16, 0.33, mat(shade(colour, -0.15))));
  // posts and the awning, sloping to the front
  for (const [x, z, h] of [[-W / 2, -D / 2, 2.35], [W / 2, -D / 2, 2.35], [-W / 2, 0.45, 2.05], [W / 2, 0.45, 2.05]] as number[][]) g.add(at(cyl(0.04, 0.045, h, wood, 7), x, h / 2, z));
  const awn = new THREE.Mesh(new THREE.PlaneGeometry(W + 0.3, 1.45), new THREE.MeshStandardMaterial({ map: stripeTex(colour, '#fbf3e4'), side: THREE.DoubleSide, roughness: 0.85 }));
  awn.rotation.x = -Math.PI / 2 + 0.24; awn.position.set(0, 2.22, -0.08); g.add(awn);
  // a scalloped valance along the front
  for (let i = 0; i < 9; i++) {
    const sc = new THREE.Mesh(new THREE.CircleGeometry(0.13, 12, Math.PI, Math.PI), mat(i % 2 ? '#fbf3e4' : colour, { rough: 0.85, side: THREE.DoubleSide }));
    sc.position.set(-W / 2 - 0.15 + (i + 0.5) * ((W + 0.3) / 9), 2.03, 0.62); g.add(sc);
  }
  // the name board on top, at the back
  const board = new THREE.Mesh(new THREE.PlaneGeometry(1.7, 0.4),
    new THREE.MeshStandardMaterial({ map: letters([{ text: `${s.name}'s`, size: 64, color: '#3a2a20' }], 512, 120, '#f6e9c8'), roughness: 0.9 }));
  board.position.set(0, 2.62, -0.62); g.add(board);
  g.add(wbox(-0.9, 2.4, -0.66, 0.9, 2.42, -0.6, wood));
  // the goods, with their tags
  const items = s.items.slice(0, 5);
  items.forEach((it, i) => {
    const x = -W / 2 + 0.3 + (i + 0.5) * ((W - 0.6) / Math.max(1, items.length));
    let o: THREE.Object3D | null = null;
    if (it.material) { o = crate(it.material, it.n); o.scale.setScalar(1.3); }
    else if (it.render && SHOWN.has(it.render) && (ITEMS as any)[it.render]) { try { o = fitted((ITEMS as any)[it.render].build(), 0.66); } catch { o = null; } }
    if (!o) o = parcel(it.n);
    o.position.set(x, 0.88, 0.02); g.add(o);
    const tag = priceTag(it.price); tag.position.set(x, 0.96, 0.39); g.add(tag);
  });
  if (s.items.length > 5) g.add(at(crate('planks', 9), W / 2 + 0.45, 0, 0.1));
  g.userData.stall = s;
  return g;
}

// an empty stall, awaiting a seller: a bare counter and a folded awning
export function emptyStall(seed: number) {
  const g = new THREE.Group(), wood = mat('#8a6343', { rough: 0.8 });
  g.add(wbox(-1.15, 0, -0.2, 1.15, 0.82, 0.32, mat('#d8c3a2', { rough: 0.85 })));
  g.add(wbox(-1.19, 0.82, -0.24, 1.19, 0.88, 0.38, surf({ kind: 'planks', color: '#c9a06a', board: 0.12 })));
  for (const x of [-1.15, 1.15]) g.add(at(cyl(0.04, 0.045, 2.35, wood, 7), x, 1.175, -0.6));
  const furled = cyl(0.08, 0.08, 2.4, mat(['#e8a87c', '#9cc5a8', '#b9a3e0'][seed % 3], { rough: 0.85 }), 10);
  furled.rotation.z = Math.PI / 2; furled.position.set(0, 2.3, -0.6); g.add(furled);
  return g;
}

// ---------------------------------------------------------------------------
// the wilds

export function signboard(title: string, sub: string, color: string) {
  const g = new THREE.Group(), wood = mat('#6e4b33', { rough: 0.8 });
  g.add(at(cyl(0.07, 0.08, 2.3, wood, 8), -1.25, 1.15, -0.13), at(cyl(0.07, 0.08, 2.3, wood, 8), 1.25, 1.15, -0.13)); // stakes behind the board
  const face = new THREE.Mesh(new THREE.PlaneGeometry(2.9, 1.15),
    new THREE.MeshStandardMaterial({ map: letters([{ text: title, size: 78, color: '#fffaf0' }, { text: sub, size: 40, color: '#f6e9c8' }], 760, 300, color), roughness: 0.9 }));
  face.position.set(0, 1.62, 0.07); g.add(face);
  g.add(wbox(-1.5, 1.0, -0.04, 1.5, 2.24, 0.05, wood));
  return g;
}

// a fingerpost arm, readable from both sides
export function fingerArm(text: string) {
  const g = new THREE.Group();
  const tex = letters([{ text, size: 46, color: '#3a2a20' }], 460, 92, '#f6e9c8');
  for (const back of [false, true]) {
    const a = new THREE.Mesh(new THREE.PlaneGeometry(1.9, 0.38), new THREE.MeshStandardMaterial({ map: tex, roughness: 0.9 }));
    if (back) { a.rotation.y = Math.PI; a.position.z = -0.012; } else a.position.z = 0.012;
    g.add(a);
  }
  g.add(rbox(1.95, 0.42, 0.02, 0.01, mat('#6e4b33', { rough: 0.85 })));
  return g;
}

export function mushrooms(seed: number) {
  const g = new THREE.Group(), r = rng(seed), cap = mat('#c94a3a', { rough: 0.6 }), stem = mat('#f2ead8', { rough: 0.8 }), dot = mat('#fffaf0');
  for (let i = 0; i < 3; i++) {
    const m = new THREE.Group(), h = 0.12 + r() * 0.1;
    m.add(at(cyl(0.025, 0.035, h, stem, 6), 0, h / 2, 0));
    const c = sphere(0.08 + r() * 0.04, cap, 10, 6); c.scale.y = 0.55; c.position.y = h; m.add(c);
    const d = sphere(0.015, dot, 5, 4); d.position.set(0.03, h + 0.04, 0.02); m.add(d);
    m.position.set((r() - 0.5) * 0.5, 0, (r() - 0.5) * 0.5); g.add(m);
  }
  return g;
}

export function fallenLog(len = 2.6) {
  const g = new THREE.Group();
  const log = cyl(0.26, 0.3, len, mat('#6e4b33', { rough: 0.95 }), 10); log.rotation.z = Math.PI / 2; log.position.y = 0.26; g.add(log);
  const moss = sphere(0.22, mat('#6f8f4a', { rough: 1 }), 8, 6); moss.scale.set(1.6, 0.4, 1); moss.position.set(0.3, 0.5, 0); g.add(moss);
  return g;
}

export function ladder(h = 3.2) {
  const g = new THREE.Group(), wood = mat('#b98a5a', { rough: 0.85 });
  for (const x of [-0.25, 0.25]) g.add(at(cyl(0.03, 0.03, h, wood, 6), x, h / 2, 0));
  for (let y = 0.3; y < h; y += 0.35) g.add(at(rbox(0.5, 0.04, 0.04, 0.01, wood), 0, y, 0));
  g.rotation.x = -0.25;
  return g;
}

export function shovelAndBucket() {
  const g = new THREE.Group();
  const handle = cyl(0.025, 0.025, 1.2, mat('#b98a5a'), 6); handle.position.set(0, 0.75, 0); handle.rotation.z = 0.25; g.add(handle);
  g.add(at(rbox(0.22, 0.28, 0.03, 0.02, mat('#8d9196', { metal: 0.6, rough: 0.4 })), 0.12, 0.14, 0));
  const bucket = cyl(0.17, 0.13, 0.3, mat('#4f7a8a', { rough: 0.5, metal: 0.3 }), 14); bucket.position.set(0.6, 0.15, 0.2); g.add(bucket);
  const clayIn = new THREE.Mesh(new THREE.CircleGeometry(0.15, 12), mat('#c0643f')); clayIn.rotation.x = -Math.PI / 2; clayIn.position.set(0.6, 0.29, 0.2); g.add(clayIn);
  return g;
}

export function crates(seed: number) {
  const g = new THREE.Group(), r = rng(seed), wood = mat('#b98a5a', { rough: 0.85 });
  for (let i = 0; i < 4; i++) {
    const c = rbox(0.7, 0.55, 0.6, 0.03, wood); c.position.set((i % 2) * 0.75 + r() * 0.1, 0.28 + (i >= 2 ? 0.56 : 0), r() * 0.1); c.rotation.y = r() * 0.3; g.add(c);
  }
  return g;
}

export function bicycle() {
  const g = new THREE.Group(), steel = mat('#5d7a8a', { rough: 0.4, metal: 0.5 }), tyre = mat('#2a2826', { rough: 0.8 });
  for (const x of [-0.55, 0.55]) { const w = new THREE.Mesh(new THREE.TorusGeometry(0.34, 0.035, 6, 20), tyre); w.position.set(x, 0.36, 0); g.add(w); }
  const bar = (ax: number, ay: number, bx: number, by: number) => { const len = Math.hypot(bx - ax, by - ay); const b = cyl(0.02, 0.02, len, steel, 5); b.position.set((ax + bx) / 2, (ay + by) / 2, 0); b.rotation.z = Math.atan2(bx - ax, by - ay) * -1; g.add(b); };
  bar(-0.55, 0.36, 0, 0.36); bar(0, 0.36, 0.45, 0.8); bar(-0.55, 0.36, -0.1, 0.8); bar(-0.1, 0.8, 0.45, 0.8); bar(0.55, 0.36, 0.45, 0.8); bar(0, 0.36, -0.1, 0.8);
  g.rotation.z = 0.08;
  return g;
}

export function pool(rx: number, rz: number) {
  const g = new THREE.Group();
  const w = new THREE.Mesh(new THREE.CircleGeometry(1, 28), mat('#7fb6cf', { rough: 0.12, metal: 0.1 }));
  w.rotation.x = -Math.PI / 2; w.scale.set(rx, rz, 1); w.position.y = 0.025; w.userData.noShadow = true; g.add(w);
  const rim = new THREE.Mesh(new THREE.TorusGeometry(1, 0.06, 5, 28), mat('#b9b2a4', { rough: 1, flat: true }));
  rim.rotation.x = Math.PI / 2; rim.scale.set(rx, rz, 1); rim.position.y = 0.03; g.add(rim);
  return g;
}

export function sheep(seed = 1) {
  const g = new THREE.Group(), r = rng(seed);
  const wool = mat('#f6f2e8', { rough: 1 }), face = mat('#3a3330', { rough: 0.7 });
  for (let i = 0; i < 7; i++) { const p = sphere(0.24 + r() * 0.06, wool, 9, 7); p.position.set((r() - 0.5) * 0.5, 0.55 + r() * 0.16, (r() - 0.5) * 0.3); g.add(p); }
  const head = sphere(0.15, face, 10, 8); head.scale.set(0.9, 1, 1.25); head.position.set(0.42, 0.62, 0); g.add(head);
  for (const s of [-1, 1]) { const ear = sphere(0.06, face, 6, 5); ear.scale.set(0.5, 0.4, 1.4); ear.position.set(0.38, 0.72, s * 0.14); g.add(ear); }
  for (const [x, z] of [[-0.2, -0.12], [-0.2, 0.12], [0.2, -0.12], [0.2, 0.12]]) g.add(at(cyl(0.035, 0.03, 0.4, face, 6), x, 0.2, z));
  return g;
}

export function logPile(n = 9, seed = 2) {
  const g = new THREE.Group(), r = rng(seed), bark = mat('#7a5235', { rough: 0.9 }), end = mat('#d9b98a', { rough: 0.8 });
  let k = 0;
  for (let row = 0; row < 3; row++) for (let i = 0; i < 4 - row; i++) {
    if (k++ >= n) break;
    const log = new THREE.Group();
    log.add(cyl(0.2, 0.2, 2.0, bark, 10));
    for (const s of [-1, 1]) { const e = new THREE.Mesh(new THREE.CircleGeometry(0.19, 10), end); e.position.y = s * 1.001; e.rotation.x = s * -Math.PI / 2; log.add(e); }
    log.rotation.x = Math.PI / 2; log.position.set((i - (3 - row) / 2) * 0.42, 0.2 + row * 0.36, r() * 0.1);
    g.add(log);
  }
  return g;
}

export function leanTo() {
  const g = new THREE.Group(), wood = mat('#6e4b33', { rough: 0.85 });
  for (const [x, h] of [[-1.6, 2.2], [1.6, 2.2]]) { g.add(at(cyl(0.07, 0.08, h, wood, 7), x, h / 2, -0.9)); g.add(at(cyl(0.07, 0.08, 1.5, wood, 7), x, 0.75, 0.9)); }
  const roof = new THREE.Mesh(new THREE.PlaneGeometry(3.6, 2.1), surf({ kind: 'shake', color: '#8a6a4a' }));
  roof.material.side = THREE.DoubleSide;
  roof.rotation.x = -Math.PI / 2 + 0.33; roof.position.set(0, 1.9, 0); g.add(roof);
  const pile = logPile(9, 3); pile.position.set(0, 0, -0.3); g.add(pile);
  return g;
}

export function sawhorse() {
  const g = new THREE.Group(), wood = mat('#b98a5a', { rough: 0.85 });
  g.add(at(rbox(1.3, 0.1, 0.12, 0.02, wood), 0, 0.72, 0));
  for (const x of [-0.5, 0.5]) for (const s of [-1, 1]) { const leg = rbox(0.07, 0.85, 0.07, 0.01, wood); leg.position.set(x, 0.38, s * 0.16); leg.rotation.x = s * 0.35; g.add(leg); }
  const log = cyl(0.13, 0.13, 1.1, mat('#7a5235', { rough: 0.9 }), 9); log.rotation.z = Math.PI / 2; log.position.y = 0.88; g.add(log);
  return g;
}

export function stumpWithHatchet() {
  const g = new THREE.Group();
  g.add(at(cyl(0.34, 0.4, 0.45, mat('#7a5235', { rough: 0.9 }), 12), 0, 0.225, 0));
  g.add(at(new THREE.Mesh(new THREE.CircleGeometry(0.33, 14), mat('#d9b98a', { rough: 0.8 })), 0, 0.452, 0));
  (g.children[1] as THREE.Mesh).rotation.x = -Math.PI / 2;
  const handle = cyl(0.025, 0.03, 0.55, mat('#b98a5a'), 6); handle.position.set(0.05, 0.68, 0); handle.rotation.z = 0.5; g.add(handle);
  g.add(at(rbox(0.16, 0.12, 0.03, 0.01, mat('#8d9196', { metal: 0.6, rough: 0.35 })), -0.07, 0.48, 0));
  return g;
}

export function quarryFace(x0: number, x1: number, z: number) {
  // stone in terraces, stepping up to the back of the quarry
  const g = new THREE.Group(), st = surf({ kind: 'ashlar', color: '#b3afa6' }), st2 = surf({ kind: 'stone', color: '#9e9a92' });
  for (let i = 0; i < 3; i++) g.add(wbox(x0 + i * 0.8, 0, z - i * 2.6, x1 - i * 0.8, 1.1 + i * 1.1, z - i * 2.6 - 2.6, i % 2 ? st : st2));
  return g;
}

export function stoneBlocks(seed = 4) {
  const g = new THREE.Group(), r = rng(seed), st = surf({ kind: 'ashlar', color: '#bdb8ae' });
  for (let i = 0; i < 7; i++) {
    const b = rbox(0.6 + r() * 0.4, 0.4 + r() * 0.2, 0.5 + r() * 0.3, 0.04, st);
    b.position.set((i % 3) * 0.95 + r() * 0.2, (i >= 5 ? 0.9 : i >= 3 ? 0.5 : 0) + 0.22, Math.floor(i / 3) * 0.2 + r() * 0.2); b.rotation.y = r() * 0.4;
    g.add(b);
  }
  return g;
}

export function winch() {
  const g = new THREE.Group(), wood = mat('#6e4b33', { rough: 0.85 });
  for (const s of [-1, 1]) { const l = cyl(0.07, 0.08, 3.6, wood, 7); l.position.set(s * 0.7, 1.7, 0); l.rotation.z = -s * 0.2; g.add(l); }
  g.add(at(rbox(1.1, 0.12, 0.12, 0.02, wood), 0, 3.4, 0));
  const rope = cyl(0.015, 0.015, 2.2, mat('#c8a878'), 5); rope.position.set(0, 2.3, 0); g.add(rope);
  g.add(at(rbox(0.5, 0.36, 0.4, 0.03, surf({ kind: 'ashlar', color: '#bdb8ae' })), 0, 1.05, 0));
  return g;
}

export function stream(points: P[], width = 1.4) {
  // a ribbon of water, a touch below the grass, with pebbled banks
  const g = new THREE.Group();
  const curve = new THREE.CatmullRomCurve3(points.map(([x, z]) => new THREE.Vector3(x, 0, z)));
  const pts = curve.getPoints(60);
  const pos: number[] = [], idx: number[] = [];
  pts.forEach((p, i) => {
    const t = pts[Math.min(pts.length - 1, i + 1)].clone().sub(pts[Math.max(0, i - 1)]).normalize();
    const n = new THREE.Vector3(-t.z, 0, t.x).multiplyScalar(width / 2 * (0.85 + 0.15 * Math.sin(i * 0.7)));
    pos.push(p.x + n.x, 0.03, p.z + n.z, p.x - n.x, 0.03, p.z - n.z);
    if (i) { const a = (i - 1) * 2; idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2); }
  });
  const geo = new THREE.BufferGeometry(); geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3)); geo.setIndex(idx); geo.computeVertexNormals();
  const water = new THREE.Mesh(geo, mat('#7fb6cf', { rough: 0.15, metal: 0.1, side: THREE.DoubleSide }));
  water.userData.noShadow = true; water.userData.keep = true;
  g.add(water);
  const r = rng(5), peb = mat('#b9b2a4', { rough: 1, flat: true });
  pts.forEach((p, i) => { if (i % 3) return; for (const s of [-1, 1]) { const b = sphere(0.09 + r() * 0.07, peb, 6, 4); b.scale.y = 0.5; b.position.set(p.x + s * (width / 2 + 0.1) * (0.9 + r() * 0.3), 0.03, p.z + (r() - 0.5) * 0.4); g.add(b); } });
  return { group: g, curve };
}

export function scrapHeap(seed: number, size = 1.6) {
  const g = new THREE.Group(), r = rng(seed);
  const cols = ['#8a5a3a', '#6e6e6e', '#a0a6ac', '#c27a4a', '#7d6a5a', '#5d5f63'];
  const base = new THREE.Mesh(new THREE.ConeGeometry(size, size * 0.7, 9), mat('#7d6a5a', { rough: 1, flat: true }));
  base.position.y = size * 0.35; g.add(base);
  for (let i = 0; i < 14; i++) {
    const a = r() * Math.PI * 2, d = r() * size * 0.75, y = (1 - d / size) * size * 0.6 + 0.1;
    const m = mat(cols[i % cols.length], { rough: 0.7, metal: 0.35 });
    const o = i % 3 === 0 ? cyl(0.08 + r() * 0.08, 0.1, 0.4 + r() * 0.3, m, 8) : i % 3 === 1 ? rbox(0.35 + r() * 0.3, 0.12, 0.25, 0.02, m) : new THREE.Mesh(new THREE.TorusGeometry(0.18, 0.04, 6, 12), m);
    o.position.set(Math.cos(a) * d, y, Math.sin(a) * d); o.rotation.set(r() * 2, r() * 3, r() * 2); g.add(o);
  }
  return g;
}

export function shed() {
  const g = new THREE.Group(), wood = mat('#6e4b33', { rough: 0.85 }), tin = surf({ kind: 'seam', color: '#a7aeb3' });
  for (const [x, z] of [[-2.2, -1.4], [2.2, -1.4], [-2.2, 1.4], [2.2, 1.4]]) g.add(at(cyl(0.07, 0.08, 2.5, wood, 7), x, 1.25, z));
  const roof = wbox(-2.5, 2.45, -1.7, 2.5, 2.55, 1.7, tin); roof.rotation.z = 0.06; g.add(roof);
  g.add(wbox(-2.2, 0, -1.45, 2.2, 1.5, -1.35, surf({ kind: 'seam', color: '#9aa1a6' })));
  // shelves of sorted things under it
  for (let i = 0; i < 3; i++) { g.add(at(heap(['iron', 'tin', 'copper'][i], 8, 0.14, i + 2), -1.4 + i * 1.4, 0, -0.9)); }
  return g;
}

export function cartWheel() {
  const g = new THREE.Group(), wood = mat('#6e4b33', { rough: 0.85 });
  const rim = new THREE.Mesh(new THREE.TorusGeometry(0.55, 0.06, 8, 24), wood); g.add(rim);
  for (let i = 0; i < 6; i++) { const s = cyl(0.025, 0.025, 1.05, wood, 5); s.rotation.z = (i / 6) * Math.PI; g.add(s); }
  g.add(cyl(0.1, 0.1, 0.16, wood, 10).rotateX(Math.PI / 2));
  g.rotation.x = -0.25; g.position.y = 0.55;
  const h = new THREE.Group(); h.add(g); return h;
}

export function fence(points: P[]) {
  const g = new THREE.Group(), wood = mat('#b98a5a', { rough: 0.85 });
  for (let i = 0; i < points.length - 1; i++) {
    const [ax, az] = points[i], [bx, bz] = points[i + 1];
    const len = Math.hypot(bx - ax, bz - az), n = Math.max(1, Math.round(len / 2));
    for (let k = 0; k <= n; k++) g.add(at(cyl(0.05, 0.06, 0.9, wood, 6), ax + ((bx - ax) * k) / n, 0.45, az + ((bz - az) * k) / n));
    for (const y of [0.4, 0.75]) { const rail = rbox(len, 0.06, 0.05, 0.01, wood); rail.position.set((ax + bx) / 2, y, (az + bz) / 2); rail.rotation.y = -Math.atan2(bz - az, bx - ax); g.add(rail); }
  }
  return g;
}

// The whole island: the four places, each furnished with what it gives.
export function wildsIsland() {
  const g = new THREE.Group();
  const { x0, z0, x1, z1 } = WILDS;
  const r = rng(41);
  const [cx, cz] = CROSSING;
  // the paths: from the bridge across the island, and a crossing north to south
  const dirt = surf({ kind: 'dirt', color: '#cdb58c' });
  g.add(wbox(x0, 0.003, cz - 1, x1 - 1.5, 0.013, cz + 1, dirt));
  g.add(wbox(cx - 1, 0.003, z0 + 1.5, cx + 1, 0.013, z1 - 1.5, dirt));
  // a fingerpost at the crossing, an arm toward each place
  const post = new THREE.Group(), wood = mat('#6e4b33', { rough: 0.8 });
  post.add(at(cyl(0.08, 0.09, 3.3, wood, 8), 0, 1.65, 0));
  post.add(at(sphere(0.12, wood, 8, 6), 0, 3.32, 0));
  for (const [text, y, ang] of [['the woodlot', 3.0, Math.PI * 0.75], ['the quarry', 2.6, Math.PI * 0.25], ['the meadow', 2.2, -Math.PI * 0.75], ['the scrapyard', 1.8, -Math.PI * 0.25]] as [string, number, number][]) {
    const a = fingerArm(text); a.position.set(Math.cos(ang) * 1.0, y, -Math.sin(ang) * 1.0); a.rotation.y = ang; post.add(a);
  }
  g.add(at(post, cx - 1.6, 0, cz - 1.6));

  // ---- the woodlot: beeches, birches and pines, a log pile under a lean-to, a sawhorse
  const W = PLACES.woodlot;
  // (P36b) a clearing round each work spot, and a lane from it toward the page's camera (-x, +z), so
  // whoever's chopping there is seen, not hidden behind a pine
  const lane = (x: number, z: number, wx: number, wz: number) => { const dx = x - wx, dz = z - wz; return dz > 0 && dz < 6 && Math.abs(dx + 0.5 * dz) < 2.4; };
  const clear = (x: number, z: number) => !W.work.some(([wx, wz]) => Math.hypot(wx - x, wz - z) < 2.0 || lane(x, z, wx, wz)) && !(x > 103 && x < 110 && z > 23 && z < 28);
  let trees = 0;
  for (let i = 0; i < 60 && trees < 22; i++) {
    const x = W.x0 + 1 + r() * (W.x1 - W.x0 - 2), z = W.z0 + 0.5 + r() * (W.z1 - W.z0 - 3);
    if (!clear(x, z)) continue;
    trees++;
    const kind = ['oak', 'birch', 'pine', 'oak', 'birch'][trees % 5];
    g.add(at(Y.tree({ kind, h: 4.6 + r() * 2.4, seed: i + 30, ...(kind === 'oak' ? { leaf: '#7d9a4f' } : {}) } as any), x, 0, z));
  }
  for (let i = 0; i < 10; i++) { const b = Y.foliage(0.4 + r() * 0.3, 0.3 + r() * 0.2, 0.4 + r() * 0.3, ['#5a8a48', '#6f9a52', '#4f7a42'][i % 3], i + 3); b.position.set(W.x0 + 1 + r() * (W.x1 - W.x0 - 2), 0.25, W.z0 + 2 + r() * (W.z1 - W.z0 - 4)); if (clear(b.position.x, b.position.z)) g.add(b); }
  for (let i = 0; i < 5; i++) { const x = W.x0 + 2 + r() * (W.x1 - W.x0 - 4), z = W.z0 + 3 + r() * (W.z1 - W.z0 - 6); if (clear(x, z)) g.add(at(mushrooms(i + 2), x, 0, z)); }
  g.add(at(leanTo(), 106.5, 0, 25.2, -0.15));
  g.add(at(sawhorse(), 100.5, 0, 21.5, 0.5));
  g.add(at(stumpWithHatchet(), 97, 0, 25.5));
  g.add(at(fallenLog(2.8), 93.5, 0, 26.5, 0.4));
  for (let i = 0; i < 4; i++) g.add(at(heap('stick', 5, 0.2, i), 95 + i * 4.2, 0, 27.8 - (i % 2) * 0.6));
  g.add(at(signboard(W.title, W.tag, W.color), 102, 0, 29.6));

  // ---- the quarry: stone in terraces, a winch, cut blocks, a clay pit and a sandy pool
  const Q = PLACES.quarry;
  g.add(quarryFace(Q.x0, Q.x1, 12.2));
  g.add(at(ladder(3.3), 130, 0, 12.6));
  g.add(at(stoneBlocks(5), 120, 0, 14.2, 0.2));
  g.add(at(stoneBlocks(9), 133.5, 0, 13.6, -0.3));
  g.add(at(winch(), 126, 0, 13.2));
  // the clay pit, terracotta, with a ring of dug earth
  const clayPit = new THREE.Mesh(new THREE.CylinderGeometry(2.0, 1.8, 0.06, 26), mat('#b85a37', { rough: 1 }));
  clayPit.scale.z = 0.75; clayPit.position.set(125, 0.02, 23.5); g.add(clayPit);
  const inner = new THREE.Mesh(new THREE.CylinderGeometry(1.2, 1.1, 0.07, 22), mat('#9a4528', { rough: 1 }));
  inner.scale.z = 0.75; inner.position.set(125.2, 0.03, 23.6); g.add(inner);
  const rim = new THREE.Mesh(new THREE.TorusGeometry(2.0, 0.16, 6, 28), mat('#8a6a4a', { rough: 1, flat: true }));
  rim.rotation.x = Math.PI / 2; rim.scale.y = 0.75; rim.position.set(125, 0.06, 23.5); g.add(rim);
  g.add(at(shovelAndBucket(), 127.6, 0, 25.6, -0.4));
  // where the stream comes out: a little pool with a sandy bank
  const sand = new THREE.Mesh(new THREE.CylinderGeometry(2.6, 2.9, 0.06, 24), mat('#e3c48f', { rough: 1 }));
  sand.scale.z = 0.7; sand.position.set(134.5, 0.02, 25); g.add(sand);
  g.add(at(pool(1.3, 0.85), 135.5, 0, 25.6));
  for (let i = 0; i < 3; i++) g.add(at(heap(['stone', 'clay', 'sand'][i], 9, 0.26, i + 7), 121 + i * 6.5, 0, 28.2));
  g.add(at(signboard(Q.title, Q.tag, '#6e6a62'), 129.5, 0, 29.6));

  // ---- the meadow: long grass and wildflowers, blue flax, a stream with stepping stones, the crown's sheep
  const M = PLACES.meadow;
  const st = stream([[100.5, M.z0 - 0.2], [99.5, 38], [102.5, 43], [100.5, 49], [102, M.z1 + 0.6]], 1.25);
  g.add(st.group);
  g.add(Y.steppingStones([[98.9, 44.8], [99.9, 45.1], [100.9, 45.2], [101.9, 45.3], [102.9, 45.5], [103.9, 45.6]], 3));
  // flax in rows, blue-flowered
  for (let row = 0; row < 4; row++) {
    const t = Y.tallGrass(6, 0.7, 40 + row, '#7a9a5a'); t.position.set(104.5, 0, 36.4 + row * 1.15); g.add(t);
    for (let i = 0; i < 20; i++) { const fl = sphere(0.07, mat(['#7f97d6', '#9aaee6', '#6f88cc'][i % 3], { rough: 0.8 }), 6, 4); fl.position.set(104.6 + r() * 5.8, 0.5 + r() * 0.18, 36.5 + row * 1.15 + r() * 0.5); g.add(fl); }
  }
  const nearStream = (x: number, z: number) => Math.abs(x - (101 + Math.sin((z - 34) * 0.32) * 1.6)) < 1.5;
  for (let i = 0; i < 6; i++) { const x = M.x0 + 2 + r() * (M.x1 - M.x0 - 6), z = M.z0 + 6 + r() * (M.z1 - M.z0 - 9); if (nearStream(x + 1, z)) continue; const t = Y.tallGrass(2.6, 1.8, 20 + i, '#8aab5c'); t.position.set(x, 0, z); g.add(t); }
  for (let i = 0; i < 220; i++) {
    const x = M.x0 + 0.8 + r() * (M.x1 - M.x0 - 1.6), z = M.z0 + 0.8 + r() * (M.z1 - M.z0 - 1.6);
    if (nearStream(x, z) || (x > 104 && x < 111 && z < 41.4)) continue;
    const f = sphere(0.055 + r() * 0.04, mat(['#e86a92', '#f2c14e', '#ffffff', '#9b7fc9', '#f6a6b8', '#ffffff'][i % 6], { rough: 0.8 }), 6, 4);
    f.position.set(x, 0.1 + r() * 0.12, z); g.add(f);
  }
  for (let i = 0; i < 6; i++) g.add(at(sheep(i + 2), 103.8 + (i % 3) * 2.6 + r(), 0, 43.5 + Math.floor(i / 3) * 3.6 + r() * 1.5, r() * 6));
  g.add(fence([[M.x1 - 0.2, M.z0 + 1], [M.x1 - 0.2, M.z1 - 1]]));
  g.add(fence([[M.x0 + 1, M.z1 - 0.4], [98.5, M.z1 - 0.4]]));
  g.add(fence([[103.5, M.z1 - 0.4], [M.x1 - 0.2, M.z1 - 0.4]]));
  g.add(at(signboard(M.title, M.tag, M.color), 108, 0, 35.2));

  // ---- the scrapyard: heaps sorted by someone patient, a shed, a wheel, crates, barrels, an old bicycle
  const S = PLACES.scrapyard;
  g.add(at(shed(), 133, 0, 41.5));
  g.add(at(scrapHeap(3, 1.8), 121.5, 0, 38.5));
  g.add(at(scrapHeap(7, 1.5), 128.5, 0, 52));
  g.add(at(scrapHeap(11, 1.3), 135, 0, 52.5));
  g.add(at(cartWheel(), 125.5, 0, 42.5, 0.6));
  g.add(at(crates(4), 120, 0, 52.5, 0.2));
  g.add(at(bicycle(), 130, 0, 47.5, 0.9));
  for (let i = 0; i < 4; i++) g.add(at(cyl(0.3, 0.3, 0.8, mat(['#4f7a8a', '#a8503a', '#5d5f63', '#c27a4a'][i], { rough: 0.6, metal: 0.3 }), 12), 136.5 + (i % 2) * 0.7, 0.4, 46.5 + Math.floor(i / 2) * 0.7));
  g.add(fence([[S.x0 + 0.5, S.z1 - 0.4], [S.x1 - 0.5, S.z1 - 0.4]]));
  g.add(fence([[S.x1 - 0.4, S.z0 + 1], [S.x1 - 0.4, S.z1 - 0.4]]));
  g.add(at(signboard(S.title, S.tag, S.color), 124.5, 0, 35.2));
  return g;
}

// a plank bridge from the Commons' east path over to the wilds
export function wildsBridge() {
  const g = new THREE.Group();
  g.add(wbox(79.6, -0.28, 30.6, WILDS.x0 + 0.4, -0.1, 32.6, surf({ kind: 'planks', color: '#a87c4f', board: 0.2 })));
  for (const z of [30.4, 32.8]) for (let x = 80.5; x < WILDS.x0; x += 1.6) g.add(at(cyl(0.05, 0.05, 0.9, mat('#6e4b33'), 6), x, 0.25, z));
  return g;
}

// what a plazmi works at in each place, for a little puff of it as they go
export const WORK_COLOUR: Record<string, string> = { woodlot: '#c9a06a', quarry: '#cfcac0', meadow: '#f6f0e6', scrapyard: '#c27a4a' };
